import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Button, Pagination } from 'components/common';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry } from 'ag-grid-community';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import { MdMessage, MdFilterAlt, MdGroupWork } from 'react-icons/md';
import { FaSearch, FaSyncAlt, FaTimes, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { FaFileExcel } from 'react-icons/fa6';
import { getTopicsWithoutProject, topicExportExcel } from 'redux/crm/project/interactionSlice';
import AdvanceFilterDrawer, { getEmptyValues } from 'components/common/AdvanceFilterDrawer';
import TopicForm from 'components/app/crm/project/interactions/TopicForm';
import columnDefs from 'components/app/crm/project/interactions/columnDefs';
import { debounce } from 'helpers/debounce';

ModuleRegistry.registerModules([AllEnterpriseModule]);

// ── Constants ────────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: '',  label: 'All' },
  { value: '1', label: 'Open' },
  { value: '2', label: 'Closed' },
];

const PRIORITY_OPTIONS = [
  { value: '',  label: 'All' },
  { value: '1', label: 'Low' },
  { value: '2', label: 'Medium' },
  { value: '3', label: 'High' },
];

const PRIORITY_COLORS = { '': '', '1': '#3b82f6', '2': '#f59e0b', '3': '#ef4444' };

// Fields for the "More Filters" drawer (matches old FilterAdvanceModal)
const FILTER_FIELDS = [
  { key: 'topic_id',      label: 'Topic ID#',      type: 'text', paramKey: 'id_cont',                tagName: 'ID' },
  { key: 'topic_name',    label: 'Topic Name',     type: 'text', paramKey: 'name_i_cont',             tagName: 'Topic' },
  { key: 'description',  label: 'Description',    type: 'text', paramKey: 'description_i_cont',      tagName: 'Desc' },
  { key: 'project_name', label: 'Project Name',   type: 'text', paramKey: 'project_name_i_cont',     tagName: 'Project' },
  { key: 'building',     label: 'Building Name',  type: 'text', paramKey: 'buildings_name_cont',     tagName: 'Building' },
  {
    key: 'status', label: 'Status', type: 'select', paramKey: 'status_eq', tagName: 'Status',
    options: [{ value: '1', label: 'Open' }, { value: '2', label: 'Closed' }],
  },
  { key: 'tags',         label: 'Tags',           type: 'text', paramKey: 'tags_cont',               tagName: 'Tags' },
];

const GROUP_ROW_COL = { field: '_projectKey', rowGroup: true, hide: true };

// ── Project group row renderer ────────────────────────────────────────────────
const ProjectGroupRenderer = ({ node }) => {
  const count       = node.allLeafChildren?.length ?? 0;
  const projectName = node.allLeafChildren?.[0]?.data?.project?.name || node.key || '—';
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px',
        height: '100%', background: '#1e3a5f', color: 'white', fontWeight: 600, cursor: 'pointer',
      }}
      onClick={() => node.setExpanded(!node.expanded)}
    >
      <span style={{ fontSize: 10, opacity: 0.7 }}>
        {node.expanded ? <FaChevronDown size={9} /> : <FaChevronRight size={9} />}
      </span>
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {projectName}
      </span>
      <span style={{ fontSize: 11, color: '#93c5fd', flexShrink: 0 }}>{count} topic{count !== 1 ? 's' : ''}</span>
    </div>
  );
};
ProjectGroupRenderer.propTypes = {
  node: PropTypes.shape({
    key: PropTypes.string,
    expanded: PropTypes.bool,
    setExpanded: PropTypes.func,
    allLeafChildren: PropTypes.arrayOf(PropTypes.shape({
      data: PropTypes.shape({ project: PropTypes.shape({ name: PropTypes.string }) }),
    })),
  }).isRequired,
};

// ── Chip button (Priority / Status quick filter) ──────────────────────────────
const ChipGroup = ({ options, value, onChange, colorMap }) => (
  <div className="tw-flex tw-gap-1">
    {options.map((opt) => {
      const active = value === opt.value;
      const color  = colorMap?.[opt.value];
      return (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(active && opt.value !== '' ? '' : opt.value)}
          className={`tw-px-2.5 tw-py-1 tw-rounded-md tw-text-xs tw-font-medium tw-border tw-cursor-pointer tw-transition-all tw-whitespace-nowrap ${
            active
              ? 'tw-text-white tw-border-transparent tw-bg-primary-500'
              : 'tw-bg-white tw-border-slate-200 tw-text-slate-600 hover:tw-border-primary-300 hover:tw-text-primary-600'
          }`}
          style={active && color ? { backgroundColor: color, borderColor: color } : undefined}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);
ChipGroup.propTypes = {
  options:  PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })).isRequired,
  value:    PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  colorMap: PropTypes.objectOf(PropTypes.string),
};
ChipGroup.defaultProps = { colorMap: null };

// ── Main page ────────────────────────────────────────────────────────────────
const InteractionPage = () => {
  const dispatch = useDispatch();

  const rowData             = useSelector((s) => s.crm_project_interaction.data);
  const totalRows           = useSelector((s) => s.crm_project_interaction.totalRows);
  const loading             = useSelector((s) => s.crm_project_interaction.loading);
  const exporting           = useSelector((s) => s.crm_project_interaction.exporting);
  const totalPagesFromStore = useSelector((s) => s.crm_project_interaction.paginate.total_pages);

  const [page, setPage]               = useState(1);
  const [perPage, setPerPage]         = useState(30);
  const [sort, setSort]               = useState({ attribute: 'id', direction: 'desc' });
  const [search, setSearch]           = useState('');
  const [filterValues, setFilterValues] = useState(() => getEmptyValues(FILTER_FIELDS));
  const [filterTags, setFilterTags]   = useState([]);
  const [showFilter, setShowFilter]   = useState(false);
  const [groupByProject, setGroupByProject] = useState(true);

  // Quick filters — status defaults to Open ('1')
  const [quickStatus,   setQuickStatus]   = useState('1');
  const [quickPriority, setQuickPriority] = useState('');

  const [showTopicForm, setShowTopicForm] = useState(false);

  const activeFilterParamsRef = useRef({});
  const searchRef             = useRef('');
  const quickRef              = useRef({ status: '1', priority: '' });

  const totalPages = Math.max(totalPagesFromStore || 1, 1);

  // Annotate rows with grouping key
  const processedRowData = useMemo(() =>
    (rowData || []).map((row) => ({ ...row, _projectKey: row.project?.name || 'No Project' })),
    [rowData],
  );

  // Column defs — add grouping column when Group by Project is active
  const COLUMN_DEFS = useMemo(() => {
    if (!groupByProject) return columnDefs;
    return [
      GROUP_ROW_COL,
      ...columnDefs.map((col) =>
        col.field === 'project.name' ? { ...col, hide: true } : col,
      ),
    ];
  }, [groupByProject]);

  // Build quick filter params
  const buildQuickParams = () => {
    const p = {};
    if (quickRef.current.status)   p.status_eq   = quickRef.current.status;
    if (quickRef.current.priority) p.priority_eq = quickRef.current.priority;
    return p;
  };

  const buildParams = useCallback(
    (overrides = {}) => ({
      page,
      per_page: groupByProject ? 1000 : perPage,
      ...(sort.attribute && { sort }),
      ...(searchRef.current && { name_or_description_i_cont: searchRef.current }),
      ...buildQuickParams(),
      ...activeFilterParamsRef.current,
      ...overrides,
    }),
    [page, perPage, sort, groupByProject],
  );

  const fetchData = useCallback((params) => {
    const flat = {};
    Object.entries(params).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '') return;
      if (typeof v === 'object' && !Array.isArray(v)) {
        Object.entries(v).forEach(([sk, sv]) => {
          if (sv !== null && sv !== undefined && sv !== '') flat[`${k}[${sk}]`] = sv;
        });
      } else {
        flat[k] = v;
      }
    });
    dispatch(getTopicsWithoutProject({ params: new URLSearchParams(flat).toString() }));
  }, [dispatch]);

  useEffect(() => { fetchData(buildParams()); }, [fetchData, buildParams]);

  const debouncedFetch = useRef(debounce((params) => fetchData(params), 500)).current;

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleSearchChange = (e) => {
    const val = e.target.value;
    searchRef.current = val;
    setSearch(val);
    setPage(1);
    debouncedFetch({ page: 1, per_page: groupByProject ? 1000 : perPage, ...(sort.attribute && { sort }), ...(val && { name_or_description_i_cont: val }), ...buildQuickParams(), ...activeFilterParamsRef.current });
  };

  const handleClearSearch = () => {
    searchRef.current = '';
    setSearch('');
    setPage(1);
    fetchData({ page: 1, per_page: groupByProject ? 1000 : perPage, ...(sort.attribute && { sort }), ...buildQuickParams(), ...activeFilterParamsRef.current });
  };

  const handleSort = useCallback(({ colId, sort: dir }) => {
    const newSort = dir ? { attribute: colId, direction: dir } : { attribute: 'id', direction: 'desc' };
    setSort(newSort);
    setPage(1);
    fetchData({ page: 1, per_page: groupByProject ? 1000 : perPage, sort: newSort, ...(searchRef.current && { name_or_description_i_cont: searchRef.current }), ...buildQuickParams(), ...activeFilterParamsRef.current });
  }, [fetchData, perPage, groupByProject]);

  const mkQuickHandler = (key, setter) => (val) => {
    quickRef.current = { ...quickRef.current, [key]: val };
    setter(val);
    setPage(1);
    fetchData({ page: 1, per_page: groupByProject ? 1000 : perPage, ...(sort.attribute && { sort }), ...(searchRef.current && { name_or_description_i_cont: searchRef.current }), ...buildQuickParams(), ...activeFilterParamsRef.current });
  };
  const handleQuickStatus   = mkQuickHandler('status',   setQuickStatus);
  const handleQuickPriority = mkQuickHandler('priority', setQuickPriority);

  const handleApplyFilter = (params, tags) => {
    activeFilterParamsRef.current = params;
    setFilterTags(tags);
    setPage(1);
    setShowFilter(false);
    fetchData({ page: 1, per_page: groupByProject ? 1000 : perPage, ...(sort.attribute && { sort }), ...(searchRef.current && { name_or_description_i_cont: searchRef.current }), ...buildQuickParams(), ...params });
  };

  const handleResetFilter = () => {
    activeFilterParamsRef.current = {};
    setFilterValues(getEmptyValues(FILTER_FIELDS));
    setFilterTags([]);
    setPage(1);
    fetchData({ page: 1, per_page: groupByProject ? 1000 : perPage, ...(sort.attribute && { sort }), ...(searchRef.current && { name_or_description_i_cont: searchRef.current }), ...buildQuickParams() });
  };

  const removeTag = (tag) => {
    const newParams = Object.fromEntries(Object.entries(activeFilterParamsRef.current).filter(([k]) => k !== tag.id));
    activeFilterParamsRef.current = newParams;
    setFilterTags((prev) => prev.filter((t) => t.id !== tag.id));
    setPage(1);
    fetchData({ page: 1, per_page: groupByProject ? 1000 : perPage, ...(sort.attribute && { sort }), ...(searchRef.current && { name_or_description_i_cont: searchRef.current }), ...buildQuickParams(), ...newParams });
  };

  const handleToggleGroup = () => {
    const next = !groupByProject;
    setGroupByProject(next);
    setPage(1);
    // When entering group mode, fetch all data
    fetchData({ page: 1, per_page: next ? 1000 : perPage, ...(sort.attribute && { sort }), ...(searchRef.current && { name_or_description_i_cont: searchRef.current }), ...buildQuickParams(), ...activeFilterParamsRef.current });
  };


  const handleGridSort = useCallback((event) => {
    const model = event.api.getSortModel?.() || [];
    handleSort(model.length > 0 ? model[0] : { colId: null, sort: null });
  }, [handleSort]);

  const handleExport = useCallback(() => {
    const flat = {};
    const params = { ...buildQuickParams(), ...activeFilterParamsRef.current, ...(searchRef.current && { name_or_description_i_cont: searchRef.current }) };
    Object.entries(params).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') flat[k] = v;
    });
    dispatch(topicExportExcel(new URLSearchParams(flat).toString()));
  }, [dispatch]);

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">

      {/* Header */}
      <div className="tw-mb-1">
        <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-w-1.5 tw-h-10 tw-rounded-sm tw-flex-shrink-0" style={{ background: 'linear-gradient(to bottom, #0ea5e9, #6366f1)' }} />
            <div className="tw-flex tw-items-center tw-gap-2">
              <MdMessage className="tw-text-primary-600" size={22} />
              <div>
                <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">Project Interactions</h1>
                <p className="tw-text-gray-500 tw-text-sm tw-mb-0">Manage and view all interactions related to your projects.</p>
              </div>
            </div>
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Button variant="secondary" size="md" leftIcon={FaSyncAlt} loading={loading} onClick={() => fetchData(buildParams())}>
              Reload
            </Button>
            <Button variant="secondary" size="md" leftIcon={FaFileExcel} disabled={exporting || loading} onClick={handleExport} className="tw-text-green-700 tw-border-green-300 tw-bg-green-50 hover:tw-bg-green-100">
              {exporting ? 'Exporting...' : 'Export'}
            </Button>
            <Button variant="primary" size="md" onClick={() => setShowTopicForm(true)}>
              + New Topic
            </Button>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2.5">

          {/* Search */}
          <div className="tw-relative tw-flex tw-items-center tw-w-full sm:tw-w-56 sm:tw-flex-shrink-0">
            <FaSearch className="tw-absolute tw-left-3 tw-text-slate-400 tw-pointer-events-none" size={12} />
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="Search topics..."
              className="tw-w-full tw-pl-8 tw-pr-7 tw-py-1.5 tw-text-sm tw-border tw-border-slate-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 focus:tw-border-transparent tw-bg-slate-50 hover:tw-bg-white tw-transition-colors tw-text-slate-700 tw-placeholder-slate-400"
            />
            {search && (
              <button type="button" onClick={handleClearSearch} className="tw-absolute tw-right-2 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-slate-300 hover:tw-bg-slate-400 tw-text-white tw-border-0 tw-cursor-pointer tw-transition-colors tw-p-0">
                <FaTimes size={8} />
              </button>
            )}
          </div>

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          {/* Quick: Status */}
          <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-shrink-0">
            <span className="tw-text-xs tw-text-slate-400 tw-font-medium">Status</span>
            <ChipGroup options={STATUS_OPTIONS} value={quickStatus} onChange={handleQuickStatus} />
          </div>

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          {/* Quick: Priority */}
          <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-shrink-0">
            <span className="tw-text-xs tw-text-slate-400 tw-font-medium">Priority</span>
            <ChipGroup options={PRIORITY_OPTIONS} value={quickPriority} onChange={handleQuickPriority} colorMap={PRIORITY_COLORS} />
          </div>

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          {/* More Filters */}
          <button
            type="button"
            onClick={() => setShowFilter(true)}
            className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded tw-border tw-text-sm tw-font-medium tw-cursor-pointer tw-transition-all tw-flex-shrink-0 ${
              filterTags.length > 0
                ? 'tw-bg-primary-600 tw-border-primary-600 tw-text-white hover:tw-bg-primary-700'
                : 'tw-bg-white tw-border-slate-200 tw-text-slate-600 hover:tw-border-primary-300 hover:tw-text-primary-600 hover:tw-bg-primary-50'
            }`}
          >
            <MdFilterAlt size={15} />
            Advanced Filters
            {filterTags.length > 0 && (
              <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[18px] tw-h-[18px] tw-px-1 tw-rounded-xl tw-bg-white/30 tw-text-white tw-text-xs tw-font-bold tw-leading-none">
                {filterTags.length}
              </span>
            )}
          </button>

          {/* Group by Project toggle */}
          <button
            type="button"
            onClick={handleToggleGroup}
            className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded tw-border tw-text-sm tw-font-medium tw-cursor-pointer tw-transition-all tw-flex-shrink-0 ${
              groupByProject
                ? 'tw-bg-blue-600 tw-border-blue-600 tw-text-white hover:tw-bg-blue-700'
                : 'tw-bg-white tw-border-slate-200 tw-text-slate-600 hover:tw-border-blue-300 hover:tw-text-blue-600 hover:tw-bg-blue-50'
            }`}
          >
            <MdGroupWork size={15} />
            {groupByProject ? 'Ungroup' : 'Group by Project'}
          </button>

          {!loading && totalRows > 0 && (
            <span className="tw-ml-auto tw-flex-shrink-0 tw-text-xs tw-text-slate-400 tw-font-medium">
              {groupByProject ? `${processedRowData.length} shown` : `${totalRows.toLocaleString()} records`}
            </span>
          )}
        </div>

        {filterTags.length > 0 && (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-border-t tw-border-slate-100 tw-bg-primary-50/40">
            <span className="tw-text-xs tw-text-slate-400 tw-font-medium tw-flex-shrink-0">Active filters:</span>
            {filterTags.map((tag) => (
              <span key={tag.id} className="tw-inline-flex tw-items-center tw-gap-1 tw-pl-2.5 tw-pr-1 tw-py-0.5 tw-bg-white tw-border tw-border-primary-200 tw-text-slate-700 tw-rounded-md tw-text-xs tw-shadow-sm">
                <span className="tw-text-primary-500 tw-font-semibold">{tag.name}</span>
                <span className="tw-text-slate-300 tw-mx-0.5">·</span>
                <span className="tw-max-w-[140px] tw-truncate tw-text-slate-600">{Array.isArray(tag.value) ? tag.value.join(' – ') : tag.value}</span>
                <button type="button" onClick={() => removeTag(tag)} className="tw-ml-0.5 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-sm tw-text-slate-400 hover:tw-bg-red-100 hover:tw-text-red-500 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-transition-colors">
                  <FaTimes size={8} />
                </button>
              </span>
            ))}
            <Button variant="secondary" size="xs" leftIcon={FaTimes} onClick={handleResetFilter} className="tw-text-red-500 tw-border-red-200 tw-bg-transparent hover:tw-bg-red-500 hover:tw-text-white hover:tw-border-red-500">
              Clear all
            </Button>
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="tw-bg-white tw-rounded tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
        {loading && (
          <div className="tw-flex tw-items-center tw-justify-center tw-p-3 tw-bg-blue-50 tw-border-b tw-border-blue-200">
            <div className="tw-animate-spin tw-rounded-xl tw-h-4 tw-w-4 tw-border-t-2 tw-border-b-2 tw-border-blue-500 tw-mr-2" />
            <span className="tw-text-sm tw-text-blue-700">Loading...</span>
          </div>
        )}
        <div
          className="ag-theme-quartz"
          style={{ height: 'max(320px, calc(100dvh - 290px))', '--ag-font-size': '13px', '--ag-font-family': 'inherit' }}
        >
          <AgGridReact
            rowData={processedRowData}
            columnDefs={COLUMN_DEFS}
            defaultColDef={{ resizable: true, sortable: false, filter: false, minWidth: 80 }}

            // Row grouping (Enterprise)
            groupDisplayType={groupByProject ? 'groupRows' : undefined}
            groupDefaultExpanded={groupByProject ? -1 : undefined}
            groupRowRenderer={groupByProject ? ProjectGroupRenderer : undefined}
            suppressAggFuncInHeader

            // Layout
            rowHeight={40}
            headerHeight={45}
            headerGroupHeight={45}

            // Interaction
            onSortChanged={handleGridSort}

            // Pagination suppressed (handled externally)
            pagination={false}
          />
        </div>
      </div>

      {/* Pagination — hidden in group mode */}
      {!groupByProject && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalResults={totalRows}
          perPage={perPage}
          pageSizeOptions={[20, 30, 50, 100]}
          onPageChange={setPage}
          onPageSizeChange={(s) => { setPerPage(s); setPage(1); }}
          disabled={loading}
        />
      )}

      <AdvanceFilterDrawer
        show={showFilter}
        onClose={() => setShowFilter(false)}
        onApply={handleApplyFilter}
        onReset={handleResetFilter}
        fields={FILTER_FIELDS}
        values={filterValues}
        onChange={(k, v) => setFilterValues((prev) => ({ ...prev, [k]: v }))}
        title="Advanced Filters — Interactions"
      />

      {showTopicForm && (
        <TopicForm
          show={showTopicForm}
          onHide={() => setShowTopicForm(false)}
          record={null}
        />
      )}
    </div>
  );
};

export default InteractionPage;
